import React, { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Grid,
  IconButton,
  Chip,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { 
  Edit as EditIcon, 
  CheckCircle as CheckCircleIcon,
  VideoLibrary as VideoIcon,
  Description as DocsIcon,
  AudioFile as AudioIcon,
  Language as WebsiteIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { FAST_API_BASE_URL } from "constants";

const StyledInput = ({ type = "text", value, onChange, name, placeholder, min, step }) => (
  <input
    type={type}
    name={name}
    value={value}
    placeholder={placeholder}
    onChange={onChange}
    min={min}
    step={step}
    style={{
      width: "100%",
      padding: "12px 16px",
      borderRadius: "10px",
      border: "2px solid #e1e5e9",
      fontSize: "0.9rem",
      outline: "none",
      boxSizing: "border-box",
      transition: "all 0.3s ease",
      backgroundColor: "#fafbfc",
    }}
    onFocus={(e) => {
      e.target.style.borderColor = "#219297";
      e.target.style.backgroundColor = "#fff";
    }}
    onBlur={(e) => {
      e.target.style.borderColor = "#e1e5e9";
      e.target.style.backgroundColor = "#fafbfc";
    }}
  />
);

const Pricing = () => {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [editPlan, setEditPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [form, setForm] = useState({
    name: "",
    websites: 1,
    externalMedia: false,
    docs: 0,
    videos: 0,
    audios: 0,
    queriesPerWebsite: "",
    customization: false,
    analytics: false,
    price: "",
    priceYear: "",
    isPopular: false,
  });

  // Fetch plans from Stripe
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${FAST_API_BASE_URL}/api/stripe/products`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      } else {
        throw new Error('Failed to fetch plans');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      showSnackbar('Error fetching plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    if (type === "checkbox") {
      setForm((p) => ({ ...p, [name]: checked }));
      return;
    }

    const numericFields = ["websites", "docs", "videos", "audios", "price", "priceYear", "queriesPerWebsite"];
    if (numericFields.includes(name)) {
      const numericValue = value === "" ? "" : Number(value);
      setForm((p) => ({ ...p, [name]: numericValue }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const openNewPlanDialog = () => {
    setForm({
      name: "",
      websites: 1,
      externalMedia: false,
      docs: 0,
      videos: 0,
      audios: 0,
      queriesPerWebsite: "",
      customization: false,
      analytics: false,
      price: "",
      priceYear: "",
      isPopular: false,
    });
    setEditPlan(null);
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const planData = {
        name: form.name || "Untitled Plan",
        price_monthly: form.price === "" ? null : form.price,
        price_yearly: form.priceYear === "" ? null : form.priceYear,
        queries_per_month: form.queriesPerWebsite === "" ? 0 : form.queriesPerWebsite,
        websites_limit: form.websites || 1,
        external_media: !!form.externalMedia,
        docs_limit: form.docs || 0,
        videos_limit: form.videos || 0,
        audios_limit: form.audios || 0,
        customization: !!form.customization,
        analytics: !!form.analytics,
        is_popular: !!form.isPopular,
        features: [
          "One Click WordPress Plugin",
          form.customization && "Full UI Customization",
          form.analytics && "User Analytics",
          form.externalMedia && "External Media Integration",
        ].filter(Boolean),
      };

      const url = editPlan ? `${FAST_API_BASE_URL}/api/stripe/products/${editPlan.id}` : `${FAST_API_BASE_URL}/api/stripe/products`;
      const method = editPlan ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(planData),
      });

      if (response.ok) {
        showSnackbar(`Plan ${editPlan ? 'updated' : 'created'} successfully`);
        fetchPlans();
        setOpen(false);
      } else {
        throw new Error('Failed to save plan');
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      showSnackbar('Error saving plan', 'error');
    }
  };

  const handleEdit = (plan) => {
    setForm({
      name: plan.name || "",
      websites: plan.websites_limit || 1,
      externalMedia: plan.external_media || false,
      docs: plan.docs_limit || 0,
      videos: plan.videos_limit || 0,
      audios: plan.audios_limit || 0,
      queriesPerWebsite: plan.queries_per_month || "",
      customization: plan.customization || false,
      analytics: plan.analytics || false,
      price: plan.prices?.month?.amount || plan.prices?.monthly?.amount || "",
      priceYear: plan.prices?.year?.amount || plan.prices?.yearly?.amount || "",
      isPopular: plan.is_popular || false,
    });
    setEditPlan(plan);
    setOpen(true);
  };

  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${FAST_API_BASE_URL}/api/stripe/products/${planToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showSnackbar('Plan deleted successfully');
        fetchPlans();
        setDeleteDialogOpen(false);
        setPlanToDelete(null);
      } else {
        throw new Error('Failed to delete plan');
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      showSnackbar('Error deleting plan', 'error');
    }
  };

  const formatPrice = (amount) => {
    if (amount === 0 || amount === null) return "Free";
    return `$${amount}`;
  };

  const getPriceLabel = (plan) => {
    const monthlyPrice = plan.prices?.month?.amount || plan.prices?.monthly?.amount;
    const yearlyPrice = plan.prices?.year?.amount || plan.prices?.yearly?.amount;
    
    if (monthlyPrice === null && yearlyPrice === null) {
      return "Contact Us";
    }
    
    if (monthlyPrice !== null) {
      return `$${monthlyPrice}/Mo`;
    }
    
    return `$${yearlyPrice}/Year`;
  };

  const getPriceDisplay = (plan) => {
    const monthlyPrice = plan.prices?.month?.amount || plan.prices?.monthly?.amount || '0';
    const yearlyPrice = plan.prices?.year?.amount || plan.prices?.yearly?.amount || '0';
    
    if (monthlyPrice === null && yearlyPrice === null) {
      return "Contact Us";
    }
    
    return {
      monthly: monthlyPrice !== null ? `$${monthlyPrice}` : null,
      yearly: yearlyPrice !== null ? `$${yearlyPrice}` : null
    };
  };

  // Transform API data to match the expected UI structure
  const transformPlanData = (plan) => {
    const monthlyPrice = plan.prices?.month?.amount || plan.prices?.monthly?.amount;
    const yearlyPrice = plan.prices?.year?.amount || plan.prices?.yearly?.amount;
    
    return {
      ...plan,
      priceLabel: getPriceLabel(plan),
      priceValue: monthlyPrice,
      priceYearValue: yearlyPrice,
      priceYearLabel: yearlyPrice !== null ? `$${yearlyPrice}/Year` : "",
      queriesLabel: plan.queries_per_month === 0 ? "Unlimited Queries/Month" : `Up to ${plan.queries_per_month} Queries/Month`,
      features: Array.isArray(plan.features) ? plan.features : []
    };
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox p={4}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <SoftTypography variant="h4" fontWeight="bold" color="text.primary" mb={1}>
            Pricing Plans
          </SoftTypography>
          <Button
            variant="contained"
            onClick={openNewPlanDialog}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "12px",
              padding: "12px 24px",
              boxShadow: "0 4px 16px rgba(33, 146, 151, 0.3)",
              "&:hover": { 
                backgroundColor: "#1a767a",
                boxShadow: "0 6px 20px rgba(33, 146, 151, 0.4)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Add New Plan
          </Button>
        </SoftBox>

        {loading ? (
          <SoftBox display="flex" justifyContent="center" alignItems="center" height="200px">
            <SoftTypography>Loading plans...</SoftTypography>
          </SoftBox>
        ) : (
          <Grid container spacing={3}>
            {plans.map((plan, i) => {
              const transformedPlan = transformPlanData(plan);
              const priceDisplay = getPriceDisplay(plan);
              
              return (
              <Grid item xs={12} sm={6} md={3} key={plan.id}>
                <SoftBox
                  sx={{
                    borderRadius: "20px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    p: 0,
                    height: "100%",
                    textAlign: "center",
                    backgroundColor: "#ffffff",
                    position: "relative",
                    border: plan.is_popular ? "2px solid #219297" : "1px solid rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                    }
                  }}
                >
                  {plan.is_popular && (
                    <Chip
                      label="Most Popular"
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        backgroundColor: "#219297",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}

                  <SoftBox sx={{ position: "absolute", top: 12, right: 12, display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      sx={{ 
                        color: "#219297",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(10px)",
                        "&:hover": { 
                          backgroundColor: "#219297",
                          color: "white",
                        },
                      }}
                      onClick={() => handleEdit(plan)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ 
                        color: "#ff6b6b",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(10px)",
                        "&:hover": { 
                          backgroundColor: "#ff6b6b",
                          color: "white",
                        },
                      }}
                      onClick={() => handleDeleteClick(plan)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </SoftBox>

                  <SoftBox sx={{ p: 3 }}>
                    <SoftTypography variant="h5" fontWeight="bold" mb={2} color="text.primary">
                      {plan.name}
                    </SoftTypography>

                    <SoftBox mb={2}>
                      {priceDisplay === "Contact Us" ? (
                        <SoftTypography 
                          sx={{ 
                            fontSize: "2.5rem", 
                            fontWeight: "bold",
                            color: "#219297",
                            lineHeight: 1,
                          }}
                        >
                          Contact Us
                        </SoftTypography>
                      ) : (
                        <>
                          {priceDisplay.monthly && (
                            <>
                              <SoftTypography 
                                sx={{ 
                                  fontSize: "2.5rem", 
                                  fontWeight: "bold",
                                  color: "#219297",
                                  lineHeight: 1,
                                }}
                              >
                                {priceDisplay.monthly}/ Month
                              </SoftTypography>
                              {/* <SoftTypography variant="body2" color="text.secondary">
                                per month
                              </SoftTypography> */}
                            </>
                          )}

                          {priceDisplay.yearly && priceDisplay.monthly && (
                            <SoftTypography sx={{ fontSize: "1.0rem", fontWeight: "bold", mt: 1 }}>
                              OR
                            </SoftTypography>
                          )}

                          {priceDisplay.yearly && (
                            <>
                              <SoftTypography 
                                sx={{ 
                                  fontSize: priceDisplay.monthly ? "2.0rem" : "2.5rem", 
                                  fontWeight: "bold",
                                  color: "#219297",
                                  lineHeight: 1,
                                }}
                              >
                                {priceDisplay.yearly}/ Year
                              </SoftTypography>
                              {/* <SoftTypography variant="body2" color="text.secondary">
                                per year
                              </SoftTypography> */}
                            </>
                          )}
                        </>
                      )}
                    </SoftBox>

                    <Chip
                      label={transformedPlan.queriesLabel}
                      sx={{
                        backgroundColor: "#f0f9fa",
                        color: "#219297",
                        fontWeight: 600,
                        borderRadius: "20px",
                        fontSize: "0.75rem",
                      }}
                    />

                    <SoftBox display="flex" alignItems="center" justifyContent="center" mt={2} gap={1}>
                      <WebsiteIcon sx={{ color: "#219297", fontSize: 18 }} />
                      <SoftTypography variant="body2" color="text.secondary">
                        {plan.websites_limit} Website{plan.websites_limit > 1 ? 's' : ''}
                      </SoftTypography>
                    </SoftBox>

                    <SoftBox textAlign="left" mt={3}>
                      {transformedPlan.features.map((feature, idx) => (
                        <SoftBox key={idx} display="flex" alignItems="flex-start" gap={1} mb={1}>
                          <CheckCircleIcon sx={{ color: "#219297", fontSize: 18, mt: 0.25 }} />
                          <SoftTypography variant="body2" color="text.primary" sx={{ fontSize: "0.85rem" }}>
                            {feature}
                          </SoftTypography>
                        </SoftBox>
                      ))}
                    </SoftBox>

                    {plan.external_media && (
                      <SoftBox 
                        sx={{
                          backgroundColor: "#f8fbfc",
                          borderRadius: "12px",
                          p: 2,
                          mt: 3,
                        }}
                      >
                        <SoftTypography variant="caption" color="text.secondary" mb={1.5} display="block" fontWeight="bold">
                          Media Allowance
                        </SoftTypography>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <SoftBox textAlign="center">
                              <DocsIcon sx={{ color: "#219297", fontSize: 20, mb: 0.5 }} />
                              <SoftTypography variant="caption" color="text.secondary" display="block">
                                Docs
                              </SoftTypography>
                              <SoftTypography variant="body2" fontWeight="bold" color="text.primary">
                                {plan.docs_limit}
                              </SoftTypography>
                            </SoftBox>
                          </Grid>
                          <Grid item xs={4}>
                            <SoftBox textAlign="center">
                              <VideoIcon sx={{ color: "#219297", fontSize: 20, mb: 0.5 }} />
                              <SoftTypography variant="caption" color="text.secondary" display="block">
                                Videos
                              </SoftTypography>
                              <SoftTypography variant="body2" fontWeight="bold" color="text.primary">
                                {plan.videos_limit}
                              </SoftTypography>
                            </SoftBox>
                          </Grid>
                          <Grid item xs={4}>
                            <SoftBox textAlign="center">
                              <AudioIcon sx={{ color: "#219297", fontSize: 20, mb: 0.5 }} />
                              <SoftTypography variant="caption" color="text.secondary" display="block">
                                Audio
                              </SoftTypography>
                              <SoftTypography variant="body2" fontWeight="bold" color="text.primary">
                                {plan.audios_limit}
                              </SoftTypography>
                            </SoftBox>
                          </Grid>
                        </Grid>
                      </SoftBox>
                    )}
                  </SoftBox>
                </SoftBox>
              </Grid>
            )})}
          </Grid>
        )}
        <Dialog 
          open={open} 
          onClose={() => setOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              padding: 2
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <SoftTypography variant="h5" fontWeight="bold">
              {editPlan ? "Edit Plan" : "Add New Plan"}
            </SoftTypography>
          </DialogTitle>

          <DialogContent dividers sx={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <SoftBox display="flex" flexDirection="column" gap={3}>
              <SoftBox>
                <SoftTypography variant="caption" color="text.secondary" mb={1} display="block">
                  Plan Name
                </SoftTypography>
                <StyledInput
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter plan name"
                />
              </SoftBox>

              <SoftBox display="flex" gap={2}>
                <SoftBox flex={1}>
                  <SoftTypography variant="caption" color="text.secondary" mb={1} display="block">
                    Number of Websites
                  </SoftTypography>
                  <StyledInput
                    name="websites"
                    type="number"
                    min={1}
                    value={form.websites}
                    onChange={handleChange}
                    placeholder="1"
                  />
                </SoftBox>

                <SoftBox flex={1}>
                  <SoftTypography variant="caption" color="text.secondary" mb={1} display="block">
                    Queries per Month
                  </SoftTypography>
                  <StyledInput
                    name="queriesPerWebsite"
                    type="number"
                    min={0}
                    value={form.queriesPerWebsite}
                    onChange={handleChange}
                    placeholder="100"
                  />
                </SoftBox>
              </SoftBox>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.externalMedia}
                    onChange={handleChange}
                    name="externalMedia"
                    sx={{ color: "#219297", "&.Mui-checked": { color: "#219297" } }}
                  />
                }
                label="External Media Integration"
              />

              {form.externalMedia && (
                <SoftBox display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                  <div>
                    <SoftTypography variant="caption" color="text.secondary" mb={1} display="block">
                      No. of Docs
                    </SoftTypography>
                    <StyledInput
                      name="docs"
                      type="number"
                      min={0}
                      value={form.docs}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <SoftTypography variant="caption" color="text.secondary" mb={1} display="block">
                      No. of Videos
                    </SoftTypography>
                    <StyledInput
                      name="videos"
                      type="number"
                      min={0}
                      value={form.videos}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <SoftTypography variant="caption" color="text.secondary" mb={1} display="block">
                      No. of Audios
                    </SoftTypography>
                    <StyledInput
                      name="audios"
                      type="number"
                      min={0}
                      value={form.audios}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                </SoftBox>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.customization}
                    onChange={handleChange}
                    name="customization"
                    sx={{ color: "#219297", "&.Mui-checked": { color: "#219297" } }}
                  />
                }
                label="Full UI Customization"
              />

              <FormControlLabel
                control={
                  <Checkbox 
                    checked={form.analytics} 
                    onChange={handleChange} 
                    name="analytics"
                    sx={{ color: "#219297", "&.Mui-checked": { color: "#219297" } }}
                  />
                }
                label="User Analytics"
              />

              <FormControlLabel
                control={
                  <Checkbox 
                    checked={form.isPopular} 
                    onChange={handleChange} 
                    name="isPopular"
                    sx={{ color: "#219297", "&.Mui-checked": { color: "#219297" } }}
                  />
                }
                label="Mark as Popular Plan"
              />

              <SoftBox>
                <SoftTypography variant="caption" color="text.secondary" mb={1} display="block">
                  Price per Month ($)
                </SoftTypography>
                <StyledInput
                  name="price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.price}
                  onChange={handleChange}
                  placeholder="0"
                />
              </SoftBox>

              <SoftBox>
                <SoftTypography variant="caption" color="text.secondary" mb={1} display="block">
                  Price per Year ($)
                </SoftTypography>
                <StyledInput
                  name="priceYear"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.priceYear}
                  onChange={handleChange}
                  placeholder="0"
                />
              </SoftBox>
            </SoftBox>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setOpen(false)}
              sx={{ 
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: "#219297",
                color: "#fff",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                "&:hover": { 
                  backgroundColor: "#1a767a",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {editPlan ? "Update Plan" : "Create Plan"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Plan</DialogTitle>
          <DialogContent>
            <SoftTypography>
              Are you sure you want to delete "{planToDelete?.name}"? This action cannot be undone.
            </SoftTypography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </SoftBox>
    </DashboardLayout>
  );
};

export default Pricing;